import { type Locale } from "@/i18n/locales"
import { getMarketCodeFromAppLocale } from "@/i18n/apps"

/**
 * Sorting preference for variant displays based on seasonal merchandising.
 * - 'inline-first': Prioritize inline variants (default behavior on regular PLPs)
 * - 'discontinued-first': Prioritize discontinued (but in-stock) variants
 * - 'sale-first': Archive/sale PLPs — on-sale variants only, best discount first.
 *   Inline/discontinued flags never rank above sale on these pages.
 * - 'default': No special sorting based on seasonal merchandising
 */
export type VariantSortPreference =
    | "inline-first"
    | "discontinued-first"
    | "sale-first"
    | "default"

type DepictVariantPricing = {
    show_as_sale?: boolean
    price_as_number?: number
    price_before_discount_as_number?: number
}

/** True when the category path is a top-level archive or sale PLP (e.g. archive-eu, sale-na). */
export function isArchiveOrSaleCategory(categories: string[]): boolean {
    return categories.some(segment => /^(archive|sale)-/.test(segment))
}

/**
 * Depict exposes sale state per variant — used on archive/sale PLPs where we cannot
 * rely on seasonal_merchandising flags or per-variant category membership.
 */
export function isVariantOnSale(variant: DepictVariantPricing): boolean {
    if (variant.show_as_sale === true) {
        return true
    }

    const sale = variant.price_as_number
    const before = variant.price_before_discount_as_number
    return typeof sale === "number" && typeof before === "number" && before > sale
}

const getVariantDiscountAmount = (variant: DepictVariantPricing): number => {
    const sale = variant.price_as_number ?? 0
    const before = variant.price_before_discount_as_number ?? 0
    return before > sale ? before - sale : 0
}

/**
 * Checks if a product should be hidden based on its seasonal merchandising status.
 * A product will be hidden if:
 * 1. It is marked as "discontinued" for the current market AND is out of stock
 *
 * @param seasonalMerch - Array of seasonal merchandising items
 * @param isInStock - Whether the product is in stock
 * @param locale - The locale string to determine the market
 * @returns boolean indicating if the product should be hidden
 */
export const shouldHideProduct = (
    seasonalMerch: (string | { label: string; value: string })[],
    isInStock: boolean,
    paramsLocale: Locale
): boolean => {
    return !seasonalMerch.some(merch => {
        const merchValue = typeof merch === "string" ? merch : merch.value
        const isDiscontinued = merchValue.toLowerCase().startsWith("discontinued")
        const merchMarket = merchValue.split("-")[1]?.toLowerCase()
        const matchesMarket = merchMarket === getMarketCodeFromAppLocale(paramsLocale)

        return isDiscontinued && !isInStock && matchesMarket
    })
}

/**
 * Checks if a product has inline seasonal merchandising for the specified market
 */
const hasInlineMerchandising = (
    seasonalMerch: (string | { label: string; value: string })[],
    paramsLocale: Locale
): boolean => {
    return seasonalMerch.some(merch => {
        const merchValue = typeof merch === "string" ? merch : merch.value
        const isInline = merchValue.toLowerCase().startsWith("inline")
        const merchMarket = merchValue.split("-")[1]?.toLowerCase()
        const matchesMarket = merchMarket === getMarketCodeFromAppLocale(paramsLocale)
        return isInline && matchesMarket
    })
}

/**
 * Checks if a product has discontinued seasonal merchandising for the specified market.
 * Note: This only checks the merchandising status, not stock status.
 * Discontinued + out-of-stock variants are filtered out separately by shouldHideProduct.
 */
const hasDiscontinuedMerchandising = (
    seasonalMerch: (string | { label: string; value: string })[],
    paramsLocale: Locale
): boolean => {
    return seasonalMerch.some(merch => {
        const merchValue = typeof merch === "string" ? merch : merch.value
        const isDiscontinued = merchValue.toLowerCase().startsWith("discontinued")
        const merchMarket = merchValue.split("-")[1]?.toLowerCase()
        const matchesMarket = merchMarket === getMarketCodeFromAppLocale(paramsLocale)
        return isDiscontinued && matchesMarket
    })
}

const isKeepableVariant = (variant: any, paramsLocale: Locale): boolean => {
    const seasonalMerch = variant.seasonal_merchandising || []

    if (!shouldHideProduct(seasonalMerch, variant.in_stock, paramsLocale)) {
        return false
    }

    // Discontinued is always discounted — never show full-price discontinued stock.
    if (
        hasDiscontinuedMerchandising(seasonalMerch, paramsLocale) &&
        !isVariantOnSale(variant)
    ) {
        return false
    }

    return true
}

const sortVariantsForPreference = (
    variants: any[],
    sortPreference: VariantSortPreference,
    paramsLocale: Locale
): any[] => {
    if (sortPreference === "default") {
        return variants
    }

    if (sortPreference === "sale-first") {
        return [...variants].sort(
            (a, b) => getVariantDiscountAmount(b) - getVariantDiscountAmount(a)
        )
    }

    if (sortPreference === "discontinued-first") {
        return [...variants].sort((a, b) => {
            const aIsDiscontinued = hasDiscontinuedMerchandising(
                a.seasonal_merchandising || [],
                paramsLocale
            )
            const bIsDiscontinued = hasDiscontinuedMerchandising(
                b.seasonal_merchandising || [],
                paramsLocale
            )
            return aIsDiscontinued ? -1 : bIsDiscontinued ? 1 : 0
        })
    }

    return [...variants].sort((a, b) => {
        const aIsInLine = hasInlineMerchandising(
            a.seasonal_merchandising || [],
            paramsLocale
        )
        const bIsInLine = hasInlineMerchandising(
            b.seasonal_merchandising || [],
            paramsLocale
        )
        return aIsInLine ? -1 : bIsInLine ? 1 : 0
    })
}

const resolveVariantIndex = (
    display: { variant_index?: number },
    filteredVariants: any[],
    sortPreference: VariantSortPreference,
    paramsLocale: Locale
): number => {
    if (filteredVariants.length === 0) {
        return 0
    }

    if (sortPreference === "sale-first") {
        return 0
    }

    if (sortPreference === "discontinued-first") {
        const discontinuedVariantIndex = filteredVariants.findIndex(variant =>
            hasDiscontinuedMerchandising(
                variant.seasonal_merchandising || [],
                paramsLocale
            )
        )

        if (discontinuedVariantIndex !== -1) {
            return discontinuedVariantIndex
        }
    } else if (sortPreference === "inline-first") {
        const inlineVariantIndex = filteredVariants.findIndex(variant =>
            hasInlineMerchandising(variant.seasonal_merchandising || [], paramsLocale)
        )

        if (inlineVariantIndex !== -1) {
            return inlineVariantIndex
        }
    }

    const currentIndex = display.variant_index ?? 0
    return currentIndex >= filteredVariants.length ? 0 : currentIndex
}

/**
 * Filters variant_displays and sorts variants based on sortPreference.
 *
 * @param displays - Depict variant display groups from listing/search/recommendations
 * @param paramsLocale - Locale used for market-scoped seasonal merchandising rules
 * @param sortPreference - Variant ordering strategy (defaults to inline-first on regular PLPs)
 *
 * @returns Filtered displays; empty groups are removed. Full-price discontinued variants
 * are always dropped. On sale-first, products with no on-sale variant are dropped,
 * only discounted variants remain, and seasonal flags do not affect order.
 */
export function filterSeasonalMerchandiseProductsByVariantDisplays(
    displays: any[],
    paramsLocale: Locale,
    sortPreference: VariantSortPreference = "inline-first"
): any[] {
    return displays
        .map(display => {
            const validVariants =
                display.variant_displays?.filter(
                    (variant: any) => variant != null && variant !== undefined
                ) || []

            let filteredVariants = validVariants.filter((variant: any) =>
                isKeepableVariant(variant, paramsLocale)
            )

            if (sortPreference === "discontinued-first") {
                filteredVariants = filteredVariants.filter((variant: any) =>
                    hasDiscontinuedMerchandising(
                        variant.seasonal_merchandising || [],
                        paramsLocale
                    )
                )
            } else if (sortPreference === "sale-first") {
                filteredVariants = filteredVariants.filter((variant: any) =>
                    isVariantOnSale(variant)
                )
            }

            filteredVariants = sortVariantsForPreference(
                filteredVariants,
                sortPreference,
                paramsLocale
            )

            const updatedVariantIndex = resolveVariantIndex(
                display,
                filteredVariants,
                sortPreference,
                paramsLocale
            )

            return {
                ...display,
                variant_displays: filteredVariants,
                variant_index: updatedVariantIndex
            }
        })
        .filter(display => display.variant_displays?.length > 0)
}
